import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSiteContext } from '../../context/SiteContext';
import { getRuntimeHost } from '../../utils/runtimeSite';
import BrandMark from './BrandMark';

const navigationLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/play', label: 'Play' },
  { to: '/leaderboard', label: 'Leaderboard' },
  { to: '/invite', label: 'Invite Friends' },
  { to: '/profile', label: 'Profile' }
];

const footerLinks = [
  { to: '/terms', label: 'Terms' },
  { to: '/privacy', label: 'Privacy' }
];

const SiteLayout: React.FC = () => {
  const location = useLocation();
  const { isSignedIn, profile, topScore } = useSiteContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hostLabel = getRuntimeHost();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.title = `${hostLabel} | Modern Snake Arcade`;
  }, [hostLabel]);

  const profileLabel = useMemo(() => {
    if (isSignedIn && profile) {
      return `Signed in as ${profile.nickname}`;
    }

    return 'Guest session';
  }, [isSignedIn, profile]);

  return (
    <div className="site-shell">
      <div className="site-shell__backdrop site-shell__backdrop--primary" />
      <div className="site-shell__backdrop site-shell__backdrop--secondary" />

      <header className="site-header">
        <div className="site-header__inner">
          <NavLink to="/" className="site-header__brand">
            <BrandMark label={hostLabel} />
          </NavLink>

          <button
            type="button"
            className="site-header__toggle"
            onClick={() => setIsMenuOpen((currentValue) => !currentValue)}
            aria-expanded={isMenuOpen}
            aria-label="Toggle site navigation"
          >
            <span />
            <span />
            <span />
          </button>

          <nav className={`site-nav ${isMenuOpen ? 'site-nav--open' : ''}`}>
            {navigationLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `site-nav__link ${isActive ? 'site-nav__link--active' : ''}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="site-header__status">
            <span className="site-chip">{profileLabel}</span>
            <span className="site-chip">Top score: {topScore}</span>
          </div>
        </div>
      </header>

      <main className="site-main">
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <div>
            <p className="site-footer__title">{hostLabel}</p>
            <p className="site-footer__copy">
              A modern arcade destination for quick sessions, player identity, and social challenges.
            </p>
          </div>

          <div className="site-footer__links">
            {footerLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `site-footer__link ${isActive ? 'site-footer__link--active' : ''}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          <p className="site-footer__meta">
            {new Date().getFullYear()} {hostLabel}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SiteLayout;
